"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { DropLocation } from "@/components/waves/drops/Drop";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropActionsOptions from "@/components/waves/drops/WaveDropActionsOptions";
import WaveDropMobileMenuDelete from "@/components/waves/drops/WaveDropMobileMenuDelete";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { MemesArtResubmitAction } from "@/components/waves/memes/submission/MemesArtResubmitAction";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MemeDropTraits from "./MemeDropTraits";
import MemesLeaderboardDropArtistInfo from "./MemesLeaderboardDropArtistInfo";
import MemesLeaderboardDropCard from "./MemesLeaderboardDropCard";
import MemesLeaderboardDropDescription from "./MemesLeaderboardDropDescription";
import MemesLeaderboardDropHeader from "./MemesLeaderboardDropHeader";
import MemesLeaderboardDropVoteSummary from "./MemesLeaderboardDropVoteSummary";
interface MemesLeaderboardDropProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly wave?: ApiWave | undefined;
  readonly location?: DropLocation | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
export const MemesLeaderboardDrop: React.FC<MemesLeaderboardDropProps> = t0 => {
  const $ = _c(119);
  const {
    drop,
    onDropClick,
    wave,
    location: t1,
    onSourceDropDeleted
  } = t0;
  const location = t1 === undefined ? DropLocation.WAVE : t1;
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    canDelete
  } = useDropInteractionRules(drop);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const openResubmitAfterMenuCloseRef = useRef(false);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let mediaImageScale = ImageScale.AUTOx800;
  if (isMobileScreen) {
    mediaImageScale = ImageScale.AUTOx450;
  } else {
    if (isTabletOrSmaller) {
      mediaImageScale = ImageScale.AUTOx600;
    }
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      openResubmitAfterMenuCloseRef.current = false;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearDeferredResubmitOpen = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      clearDeferredResubmitOpen();
    };
    t4 = [clearDeferredResubmitOpen];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== hasTouchScreen) {
    t5 = {
      hasTouchScreen,
      onInteractionStart: clearDeferredResubmitOpen
    };
    $[3] = hasTouchScreen;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t5);
  let t6;
  if ($[5] !== setIsActive) {
    t6 = () => {
      openResubmitAfterMenuCloseRef.current = true;
      setIsActive(false);
    };
    $[5] = setIsActive;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openResubmitAfterMobileMenuCloses = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!openResubmitAfterMenuCloseRef.current) {
        return;
      }
      clearDeferredResubmitOpen();
      setIsResubmitModalOpen(true);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleMobileMenuAfterLeave = t7;
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t8;
  if ($[8] !== drop.parts) {
    t8 = drop.parts.at(0)?.media.at(0);
    $[8] = drop.parts;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const artworkMedia = t8;
  let t9;
  if ($[10] !== drop.top_raters) {
    t9 = drop.top_raters.slice(0, 3);
    $[10] = drop.top_raters;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const firstThreeVoters = t9;
  let t10;
  if ($[12] !== drop || $[13] !== hasTouchScreen || $[14] !== isMobileScreen || $[15] !== onDropClick) {
    t10 = () => {
      if (hasTouchScreen) {
        return;
      }
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_memes",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[12] = drop;
    $[13] = hasTouchScreen;
    $[14] = isMobileScreen;
    $[15] = onDropClick;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== drop) {
    t11 = <MemesLeaderboardDropArtistInfo drop={drop} />;
    $[17] = drop;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== canDelete || $[20] !== drop || $[21] !== hasTouchScreen || $[22] !== onSourceDropDeleted || $[23] !== wave) {
    t12 = !hasTouchScreen && <><WaveDropActionsOpen drop={drop} /><MemesArtResubmitAction drop={drop} wave={wave} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropActionsOptions drop={drop} />}</>;
    $[19] = canDelete;
    $[20] = drop;
    $[21] = hasTouchScreen;
    $[22] = onSourceDropDeleted;
    $[23] = wave;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <div className="tw-flex tw-gap-2 tw-text-iron-400">{t12}</div>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-start tw-justify-between tw-gap-4">{t11}{t13}</div></div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = artworkMedia?.mime_type;
  let t16;
  if ($[30] !== drop.id || $[31] !== t15) {
    t16 = <MediaTypeBadge mimeType={t15} dropId={drop.id} size="sm" />;
    $[30] = drop.id;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== title) {
    t17 = <MemesLeaderboardDropHeader title={title} />;
    $[33] = title;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-2">{t16}{t17}</div>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== description) {
    t19 = <MemesLeaderboardDropDescription description={description} />;
    $[38] = description;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t18 || $[41] !== t19) {
    t20 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t18}{t19}</div></div>;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== artworkMedia || $[44] !== location || $[45] !== mediaImageScale) {
    t21 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-950" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} imageScale={mediaImageScale} /></div>;
    $[43] = artworkMedia;
    $[44] = location;
    $[45] = mediaImageScale;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== drop) {
    t22 = <MemeDropTraits drop={drop} />;
    $[47] = drop;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== drop.context_profile_context || $[50] !== drop.raters_count || $[51] !== drop.rating || $[52] !== drop.rating_prediction || $[53] !== drop.wave.voting_credit_type || $[54] !== firstThreeVoters) {
    t23 = <MemesLeaderboardDropVoteSummary current={drop.rating} projected={drop.rating_prediction} creditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={firstThreeVoters} userContext={drop.context_profile_context} />;
    $[49] = drop.context_profile_context;
    $[50] = drop.raters_count;
    $[51] = drop.rating;
    $[52] = drop.rating_prediction;
    $[53] = drop.wave.voting_credit_type;
    $[54] = firstThreeVoters;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== firstThreeVoters) {
    t24 = firstThreeVoters.map(_temp5);
    $[56] = firstThreeVoters;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t24) {
    t25 = <div className="tw-flex tw-items-center -tw-space-x-2">{t24}</div>;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== drop.raters_count) {
    t26 = formatNumberWithCommas(drop.raters_count);
    $[60] = drop.raters_count;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  const t27 = drop.raters_count === 1 ? "voter" : "voters";
  let t28;
  if ($[62] !== t27) {
    t28 = <span className="tw-font-normal tw-text-iron-500">{t27}</span>;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t26 || $[65] !== t28) {
    t29 = <span className="tw-text-xs tw-font-bold tw-text-white">{t26}{" "}{t28}</span>;
    $[64] = t26;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t25 || $[68] !== t29) {
    t30 = <div className="tw-flex tw-items-center tw-gap-2 @[700px]:tw-hidden">{t25}{t29}</div>;
    $[67] = t25;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setIsVotingModalOpen(true);
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== drop) {
    t32 = <VotingModalButton drop={drop} onClick={t31} />;
    $[71] = drop;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t30 || $[74] !== t32) {
    t33 = <div className="tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-4 @[700px]:tw-w-auto @[700px]:tw-justify-end" onClick={_temp3}>{t30}{t32}</div>;
    $[73] = t30;
    $[74] = t32;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t23 || $[77] !== t33) {
    t34 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4 @[700px]:tw-flex-row @[700px]:tw-items-center">{t23}{t33}</div>;
    $[76] = t23;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t22 || $[80] !== t34) {
    t35 = <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4">{t22}{t34}</div>;
    $[79] = t22;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t14 || $[83] !== t20 || $[84] !== t21 || $[85] !== t35) {
    t36 = <div>{t14}{t20}{t21}{t35}</div>;
    $[82] = t14;
    $[83] = t20;
    $[84] = t21;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== drop || $[88] !== isMobileScreen || $[89] !== isVotingModalOpen) {
    t37 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[87] = drop;
    $[88] = isMobileScreen;
    $[89] = isVotingModalOpen;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== drop || $[92] !== t36 || $[93] !== t37) {
    t38 = <MemesLeaderboardDropCard drop={drop}>{t36}{t37}</MemesLeaderboardDropCard>;
    $[91] = drop;
    $[92] = t36;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t38 || $[96] !== touchHandlers) {
    t39 = <div {...touchHandlers}>{t38}</div>;
    $[95] = t38;
    $[96] = touchHandlers;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] !== canDelete || $[99] !== drop || $[100] !== hasTouchScreen || $[101] !== isActive || $[102] !== onSourceDropDeleted || $[103] !== openResubmitAfterMobileMenuCloses || $[104] !== setIsActive || $[105] !== wave) {
    t40 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive} onAfterLeave={handleMobileMenuAfterLeave}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} /><WaveDropMobileMenuCopyLink drop={drop} onCopy={() => setIsActive(false)} /><MemesArtResubmitAction drop={drop} wave={wave} variant="menu" onOpenModal={openResubmitAfterMobileMenuCloses} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={() => setIsActive(false)} />}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[98] = canDelete;
    $[99] = drop;
    $[100] = hasTouchScreen;
    $[101] = isActive;
    $[102] = onSourceDropDeleted;
    $[103] = openResubmitAfterMobileMenuCloses;
    $[104] = setIsActive;
    $[105] = wave;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  let t41;
  if ($[107] !== drop || $[108] !== isResubmitModalOpen || $[109] !== onSourceDropDeleted || $[110] !== wave) {
    t41 = wave && isResubmitModalOpen && <MemesArtSubmissionModal isOpen={isResubmitModalOpen} wave={wave} sourceDrop={drop} onClose={() => setIsResubmitModalOpen(false)} onSourceDropDeleted={onSourceDropDeleted} />;
    $[107] = drop;
    $[108] = isResubmitModalOpen;
    $[109] = onSourceDropDeleted;
    $[110] = wave;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  let t42;
  if ($[112] !== t39 || $[113] !== t40 || $[114] !== t41) {
    t42 = <div className="tw-group tw-w-full">{t39}{t40}{t41}</div>;
    $[112] = t39;
    $[113] = t40;
    $[114] = t41;
    $[115] = t42;
  } else {
    t42 = $[115];
  }
  let t43;
  if ($[116] !== t10 || $[117] !== t42) {
    t43 = <div className="tw-w-full tw-cursor-pointer tw-@container" onClick={t10}>{t42}</div>;
    $[116] = t10;
    $[117] = t42;
    $[118] = t43;
  } else {
    t43 = $[118];
  }
  return t43;
};
function _temp(m) {
  return m.data_key === "title";
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(voter) {
  return <Link key={voter.profile.handle ?? voter.profile.primary_address} href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp4}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link>;
}